import {
  elGetParents,
  elGetOffset,
  elSetCSS,
  elToggleClasses,
} from '../utils/element-helpers';

/**
 * Scroller
 * v3.2.5 - Memory optimized
 */
const scroller = () => {
  const htmlEl = document.querySelector('html');

  let scrollers = {};
  
  // Reusable variables (moved inside functions to avoid hoisting issues)
  let ww, wh, mxp, myp;

  // constants
  const SCROLL_DIRECTION_BUFFER = 16; // px
  const SCROLL_TOP_DISTANCE = 32; // px
  const SCROLL_HEADER_DISTANCE = 160; // px

  const PREFIX = 'data-scroller';
  const ID = `${PREFIX}-id`;
  const ATTR_ITEM = `${PREFIX}-item`;
  const ATTR_CONTAINER = `${PREFIX}-container`;
  const ATTR_TRACE = `${PREFIX}-trace`;
  const ATTR_TRACE_OFFSCREEN = `${ATTR_TRACE}-offscreen`;
  const ATTR_SECTION = `${PREFIX}-section`;
  const ATTR_VIEWED_CUSTOM = `${PREFIX}-viewed-custom`;
  const ATTR_SECTION_CURRENT = `${ATTR_SECTION}-current`;
  const ATTR_SECTION_LABEL = `${ATTR_SECTION}-label`;

  // container states
  const IS_SCROLL_ZERO = 'is-scroll-zero';
  const IS_SCROLL_TOP = 'is-scroll-top';
  const IS_SCROLL_HEADER = 'is-scroll-header';
  const IS_SCROLLING = 'is-scrolling';
  const IS_SCROLLING_UP = `${IS_SCROLLING}--up`;
  const IS_SCROLLING_DOWN = `${IS_SCROLLING}--down`;
  const IS_SCROLLING_LEFT = `${IS_SCROLLING}--left`;
  const IS_SCROLLING_RIGHT = `${IS_SCROLLING}--right`;

  // item states
  const IS_VIEWED = 'is-viewed';
  const IS_VIEWED_CUSTOM = 'is-viewed-custom';
  const IS_SPREAD = 'is-spread';
  const IS_SPREAD_X = `${IS_SPREAD}--x`;
  const IS_SPREAD_Y = `${IS_SPREAD}--y`;
  const IS_CONTAINED = 'is-contained';
  const IS_CONTAINED_X = `${IS_CONTAINED}--x`;
  const IS_CONTAINED_Y = `${IS_CONTAINED}--y`;
  const IS_OFFSCREEN = 'is-offscreen';
  const IS_OFFSCREEN_ABOVE = `${IS_OFFSCREEN}--above`;
  const IS_OFFSCREEN_BELOW = `${IS_OFFSCREEN}--below`;
  const IS_OFFSCREEN_BEFORE = `${IS_OFFSCREEN}--before`;
  const IS_OFFSCREEN_AFTER = `${IS_OFFSCREEN}--after`;
  const IS_ENTERING = 'is-entering';
  const IS_ENTERING_ABOVE = `${IS_ENTERING}--above`;
  const IS_ENTERING_BELOW = `${IS_ENTERING}--below`;
  const IS_ENTERING_BEFORE = `${IS_ENTERING}--before`;
  const IS_ENTERING_AFTER = `${IS_ENTERING}--after`;
  const IS_ENTERED = 'is-entered';
  const IS_ENTERED_ABOVE = `${IS_ENTERED}--above`;
  const IS_ENTERED_BELOW = `${IS_ENTERED}--below`;
  const IS_ENTERED_BEFORE = `${IS_ENTERED}--before`;
  const IS_ENTERED_AFTER = `${IS_ENTERED}--after`;
  const IS_EXITING = 'is-exiting';
  const IS_EXITING_ABOVE = `${IS_EXITING}--above`;
  const IS_EXITING_BELOW = `${IS_EXITING}--below`;
  const IS_EXITING_BEFORE = `${IS_EXITING}--before`;
  const IS_EXITING_AFTER = `${IS_EXITING}--after`;

  // events
  const EVENT_SECTIONS_INIT = 'scroller-sections-init';
  const EVENT_SECTION_UPDATE = 'scroller-section-update';
  const EVENT_VIEWED_FIRST = 'scroller-viewed-first';
  const EVENT_VIEWED_CUSTOM = 'scroller-viewed-custom';

  /**
   * Update Scroll Container
   * @param {Object} data
   */
  const updateContainer = (data) => {
    const { state } = data;
    const {
      scrollLeft,
      scrollTop,
      clientWidth,
      clientHeight,
      scrollWidth,
      scrollHeight,
    } = data.el;

    const scrollLeftMax = scrollWidth - clientWidth;
    const scrollLeftPercent = scrollLeft / scrollLeftMax || 0;
    const scrollTopMax = scrollHeight - clientHeight;
    const scrollTopPercent = scrollTop / scrollTopMax;
    const scrollDirectionRight = state.scrollLeft < scrollLeft;
    const scrollDirectionDown = state.scrollTop < scrollTop;
    const distanceTop = Math.abs(state.scrollTopPivot - scrollTop);
    const distanceLeft = Math.abs(state.scrollLeftPivot - scrollLeft);
    const isPassedBufferY = distanceTop > SCROLL_DIRECTION_BUFFER;
    const isPassedBufferX = distanceLeft > SCROLL_DIRECTION_BUFFER;
    
    const isScrollingRight = isPassedBufferX
      ? scrollDirectionRight
      : state.isScrollingRight;
    const isScrollingDown = isPassedBufferY
      ? scrollDirectionDown
      : state.isScrollingDown;
    
    let scrollTopPivot = state.scrollTopPivot || 0;
    let scrollLeftPivot = state.scrollLeftPivot || 0;

    if (state.isScrollingDown !== scrollDirectionDown && isPassedBufferY) {
      scrollTopPivot = scrollTop;
    }

    if (state.isScrollingRight !== scrollDirectionRight && isPassedBufferX) {
      scrollLeftPivot = scrollLeft;
    }

    // update state
    data.state = {
      ...data.state,
      scrollLeft,
      scrollTop,
      scrollTopPivot,
      scrollLeftPivot,
      isScrollingDown,
      isScrollingRight,
      clientWidth,
      clientHeight,
      scrollWidth,
      scrollHeight,
    };

    // clear and set timeout
    clearTimeout(data.timeout);
    data.timeout = setTimeout(() => {
      elToggleClasses(data.el, {
        [IS_SCROLLING]: false,
      });
    }, 1000);

    // update classes
    elToggleClasses(data.el, {
      [IS_SCROLLING]: true,
      [IS_SCROLL_ZERO]: scrollTop === 0,
      [IS_SCROLL_TOP]: scrollTop < SCROLL_TOP_DISTANCE,
      [IS_SCROLL_HEADER]: scrollTop < SCROLL_HEADER_DISTANCE,
      [IS_SCROLLING_UP]: !isScrollingDown,
      [IS_SCROLLING_DOWN]: isScrollingDown,
      [IS_SCROLLING_LEFT]: !isScrollingRight,
      [IS_SCROLLING_RIGHT]: isScrollingRight,
    });
  };

  /**
   * Update Item
   * @param {Element} el
   * @param {Object} data
   */
  const updateItem = (el, data) => {
    const {
      scrollLeft: x,
      scrollTop: y,
      clientWidth: w,
      clientHeight: h,
      isScrollingDown,
      isScrollingRight,
    } = data.state;

    const x2 = x + w;
    const y2 = y + h;
    const itemOffset = elGetOffset(el);
    const itemX = itemOffset.left;
    const itemY = itemOffset.top;
    const itemW = el.offsetWidth ?? el.clientWidth;
    const itemH = el.offsetHeight ?? el.clientHeight;
    
    const i = {
      x: itemX,
      y: itemY,
      w: itemW,
      h: itemH,
      x2: itemX + itemW,
      y2: itemY + itemH,
    };

    const isAbove2 = i.y2 < y;
    const isBelow = i.y > y2;
    const isBefore2 = i.x2 < x;
    const isAfter = i.x > x2;
    const isOffscreen = isAbove2 || isBelow || isBefore2 || isAfter;
    const wasOffscreen = el.classList.contains(IS_OFFSCREEN);

    if (wasOffscreen && isOffscreen) return;

    const isAbove = i.y < y;
    const isBelow2 = i.y2 > y2;
    const isBefore = i.x < x;
    const isAfter2 = i.x2 > x2;
    const isSpreadY = isAbove && isBelow2;
    const isContainedY = !isAbove && !isBelow2;
    const isEnteredAbove = isAbove2;
    const isEnteringAbove = isAbove && !isAbove2 && !isScrollingDown;
    const isExitingAbove = isAbove && !isAbove2 && isScrollingDown;
    const isEnteredBelow = !isBelow2;
    const isEnteringBelow = !isBelow && isBelow2 && isScrollingDown;
    const isExitingBelow = !isBelow && isBelow2 && !isScrollingDown;
    const isSpreadX = isBefore && isAfter2;
    const isContainedX = !isBefore && !isAfter2;
    const isEnteredBefore = isBefore2;
    const isEnteringBefore = isBefore && !isBefore2 && !isScrollingRight;
    const isExitingBefore = isBefore && !isBefore2 && isScrollingRight;
    const isEnteredAfter = !isAfter2;
    const isEnteringAfter = !isAfter && isAfter2 && isScrollingRight;
    const isExitingAfter = !isAfter && isAfter2 && !isScrollingRight;
    const beenViewed = el.classList.contains(IS_VIEWED);
    const beenViewedCustom = el.classList.contains(IS_VIEWED_CUSTOM);

    const elEvents = el.getAttribute('data-scroller-events');

    // Tracing
    const tracing = el.getAttribute(ATTR_TRACE);
    const doTraceOffscreen = el.getAttribute(ATTR_TRACE_OFFSCREEN) != undefined;
    
    if (tracing) {
      if (!(isAbove2 || isBelow) || doTraceOffscreen) {
        const traceCss = {};

        if (tracing.includes('bttb') || tracing == '') {
          traceCss['--bttb'] = (y - (i.y - wh)) / (wh + i.h);
        }
        if (tracing.includes('bttt')) {
          traceCss['--bttt'] = (y - (i.y - wh)) / wh;
        }
        if (tracing.includes('btbb')) {
          traceCss['--btbb'] = (y - (i.y - wh)) / i.h;

          if (elEvents && elEvents.includes('btbb')) {
            el.dispatchEvent(
              new CustomEvent('scroller-trace-btbb', {
                detail: { value: traceCss['--btbb'] },
              })
            );
          }
        }
        if (tracing.includes('bbtt')) {
          traceCss['--bbtt'] = (y - (i.y - wh + i.h)) / (wh - i.h);
        }
        if (tracing.includes('tttb')) {
          traceCss['--tttb'] = (y - i.y) / i.h;
        }
        if (tracing.includes('ttbb')) {
          traceCss['--ttbb'] = (y - i.y) / (i.h - wh);
        }

        elSetCSS(el, traceCss);
      }

      // Offscreen clamps
      if (tracing.includes('tttb') && (isAbove2 || isBelow) && !doTraceOffscreen) {
        elSetCSS(el, { '--tttb': isAbove2 ? 1 : 0 });
      }

      if (tracing.includes('btbb') && isBelow && !doTraceOffscreen) {
        elSetCSS(el, { '--btbb': 0 });
        if (elEvents && elEvents.includes('btbb')) {
          el.dispatchEvent(
            new CustomEvent('scroller-trace-btbb', {
              detail: { value: 0 },
            })
          );
        }
      }
    }

    // Custom viewed threshold
    const viewedCustom = el.getAttribute(ATTR_VIEWED_CUSTOM);
    if (viewedCustom && !beenViewedCustom) {
      if ((y - (i.y - wh)) / i.h >= parseFloat(viewedCustom)) {
        el.classList.add(IS_VIEWED_CUSTOM);
        el.dispatchEvent(new CustomEvent(EVENT_VIEWED_CUSTOM));
      }
    }

    // Events
    if (isContainedX && isContainedY && !beenViewed) {
      el.dispatchEvent(new CustomEvent(EVENT_VIEWED_FIRST));
    }

    if (elEvents) {
      if (
        elEvents.includes(IS_ENTERING_BELOW) &&
        isEnteringBelow &&
        !el.classList.contains(IS_ENTERING_BELOW)
      ) {
        el.dispatchEvent(new CustomEvent('scroller-' + IS_ENTERING_BELOW));
      }

      if (
        elEvents.includes(IS_EXITING_BELOW) &&
        isExitingBelow &&
        !el.classList.contains(IS_EXITING_BELOW)
      ) {
        el.dispatchEvent(new CustomEvent('scroller-' + IS_EXITING_BELOW));
      }

      if (
        elEvents.includes(IS_CONTAINED_Y) &&
        isContainedY &&
        !el.classList.contains(IS_CONTAINED_Y)
      ) {
        el.dispatchEvent(new CustomEvent('scroller-' + IS_CONTAINED_Y));
      }

      if (
        elEvents.includes(IS_OFFSCREEN_ABOVE) &&
        isAbove2 &&
        !el.classList.contains(IS_OFFSCREEN_ABOVE)
      ) {
        el.dispatchEvent(new CustomEvent('scroller-' + IS_OFFSCREEN_ABOVE));
      }
    }

    // Toggle classes
    elToggleClasses(el, {
      [IS_VIEWED]: beenViewed || (isContainedX && isContainedY),
      [IS_SPREAD]: isSpreadX || isSpreadY,
      [IS_SPREAD_X]: isSpreadX,
      [IS_SPREAD_Y]: isSpreadY,
      [IS_CONTAINED]: isContainedX || isContainedY,
      [IS_CONTAINED_X]: isContainedX,
      [IS_CONTAINED_Y]: isContainedY,
      [IS_OFFSCREEN]: isOffscreen,
      [IS_OFFSCREEN_ABOVE]: isAbove2,
      [IS_OFFSCREEN_BELOW]: isBelow,
      [IS_OFFSCREEN_BEFORE]: isBefore2,
      [IS_OFFSCREEN_AFTER]: isAfter,
      [IS_ENTERING]:
        isEnteringAbove ||
        isEnteringBelow ||
        isEnteringBefore ||
        isEnteringAfter,
      [IS_ENTERING_ABOVE]: isEnteringAbove,
      [IS_ENTERING_BELOW]: isEnteringBelow,
      [IS_ENTERING_BEFORE]: isEnteringBefore,
      [IS_ENTERING_AFTER]: isEnteringAfter,
      [IS_ENTERED]:
        isEnteredAbove || isEnteredBelow || isEnteredBefore || isEnteredAfter,
      [IS_ENTERED_ABOVE]: isEnteredAbove,
      [IS_ENTERED_BELOW]: isEnteredBelow,
      [IS_ENTERED_BEFORE]: isEnteredBefore,
      [IS_ENTERED_AFTER]: isEnteredAfter,
      [IS_EXITING]:
        isExitingAbove || isExitingBelow || isExitingBefore || isExitingAfter,
      [IS_EXITING_ABOVE]: isExitingAbove,
      [IS_EXITING_BELOW]: isExitingBelow,
      [IS_EXITING_BEFORE]: isExitingBefore,
      [IS_EXITING_AFTER]: isExitingAfter,
    });
  };

  /**
   * Update Items
   * @param {Object} data
   */
  const updateItems = (data) => {
    data.items.forEach((el) => {
      updateItem(el, data);
    });

    if (!data.children.length) return;

    for (const value of Object.values(data.children)) {
      updateItem(value.el, data);
      updateItems(value);
    }
  };

  /**
   * Update Sections
   * @param {Object} data
   */
  const updateSections = (data) => {
    if (!data.sections.length) return;

    const previousSectionId = data.el.getAttribute(ATTR_SECTION_CURRENT);
    let currentSectionEl = null;

    data.sections
      .slice()
      .reverse()
      .forEach((el) => {
        if (!currentSectionEl && data.state.scrollTop >= elGetOffset(el).top) {
          currentSectionEl = el;
        }
      });

    const currentSectionId = currentSectionEl
      ? currentSectionEl.getAttribute(ATTR_SECTION)
      : null;

    if (previousSectionId != currentSectionId) {
      data.el.setAttribute(ATTR_SECTION_CURRENT, currentSectionId);
      data.el.dispatchEvent(
        new CustomEvent(EVENT_SECTION_UPDATE, {
          detail: {
            el: currentSectionEl,
            id: currentSectionId,
            label: currentSectionEl
              ? currentSectionEl.getAttribute(ATTR_SECTION_LABEL)
              : null,
          },
        })
      );
    }
  };

  /**
   * On Scroll
   * @param {Object} data
   */
  const onScroll = (data) => {
    if (!data) return;
    updateContainer(data);
    updateItems(data);
    updateSections(data);
  };

  const setWindowSize = () => {
    ww = window.innerWidth;
    wh = window.innerHeight;
  };

  /**
   * On Resize
   * @param {Object} data
   */
  const onResize = (data) => {
    setWindowSize();
    onScroll(data);
  };

  /**
   * Build Tree
   * @param {String[]} tree
   */
  const buildTree = (tree) => {
    let obj = scrollers;

    tree.forEach((id) => {
      if (obj[id]) {
        obj = obj[id].children;
      } else {
        const el = document.querySelector(`[${ATTR_CONTAINER}][${ID}="${id}"]`);
        const scrollerEl = id === 'html' ? window : el;

        const data = (obj[id] = {
          scrollerEl,
          el,
          state: {
            scrollLeft: 0,
            scrollTop: 0,
            scrollTopPivot: 0,
            scrollLeftPivot: 0,
            isScrollingDown: true,
            isScrollingRight: true,
          },
          items: [],
          sections: [],
          children: {},
          timeout: null,
        });

        scrollerEl.addEventListener('scroll', () => {
          onScroll(data);
        });
        window.addEventListener('resize', () => {
          onResize(data);
        });
      }
    });
  };

  /**
   * Get Tree Ids
   * @param {Element} el
   */
  const getTreeIds = (el) => {
    const itemParentEls = elGetParents(el, `[${ATTR_CONTAINER}]`);
    const treeIds = itemParentEls.reverse().map((el) => el.getAttribute(ID));
    return treeIds;
  };

  /**
   * Init
   */
  const init = () => {
    const itemEls = document.querySelectorAll(`[${ATTR_ITEM}]`);
    const sectionEls = document.querySelectorAll(`[${ATTR_SECTION}]`);

    htmlEl.setAttribute(ID, 'html');
    htmlEl.setAttribute(ATTR_CONTAINER, '');

    setWindowSize();

    // Mouse tracking
    if (htmlEl.getAttribute('data-scroller-trace') == 'mouse') {
      htmlEl.addEventListener('mousemove', ({ clientX: mx, clientY: my }) => {
        mxp = Math.max(0, Math.min(1, mx / ww));
        myp = Math.max(0, Math.min(1, my / wh));

        elSetCSS(htmlEl, {
          '--mx': mxp,
          '--my': myp,
        });
      });
    }

    // Build tree
    itemEls.forEach((el) => {
      const tree = getTreeIds(el);
      buildTree(tree);
    });

    // Add items
    itemEls.forEach((el) => {
      const tree = getTreeIds(el);
      const obj = tree.reduce((o, v) => o[v] || o.children[v], scrollers);
      obj.items.push(el);
    });

    // Add sections
    sectionEls.forEach((el) => {
      const tree = getTreeIds(el);
      const obj = tree.reduce((o, v) => o[v] || o.children[v], scrollers);
      obj.sections.push(el);
    });

    // Init events
    for (const value of Object.values(scrollers)) {
      if (!value.sections.length) continue;

      value.el.dispatchEvent(
        new CustomEvent(EVENT_SECTIONS_INIT, {
          detail: {
            sections: value.sections.map((el) => {
              return {
                id: el.getAttribute(ATTR_SECTION),
                label: el.getAttribute(ATTR_SECTION_LABEL),
              };
            }),
          },
        })
      );
    }
  };

  init();
};

document.addEventListener('DOMContentLoaded', scroller);

export default scroller;